import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import getTabsByRole from "../../../../../common/utils/UserWiseRole";
import RolesId from "../../../../../common/enums/RoleIds";

interface DeviationTableProps {
  month: number;
  year: number;
  ga_code: string;
  userTourCardData: any;
  userRoleId: number;
  autoSelectedTab?: string; 
  onTabSwitchTour?: (payload: any) => void;
  onTabSwitchUser?: (payload: any) => void; 
}

const DeviationTable: React.FC<DeviationTableProps> = ({
  userTourCardData,
  userRoleId,
  month,
  year,
  autoSelectedTab,
  onTabSwitchTour,
  onTabSwitchUser,
  ga_code,
}) => {
  const allowedTabs = getTabsByRole(userRoleId);

  const [selectedTab, setSelectedTab] = useState(
    autoSelectedTab && allowedTabs.includes(autoSelectedTab)
      ? autoSelectedTab
      : allowedTabs[0]
  );

  const [sortOrder, setSortOrder] = useState<"Highest" | "Lowest">("Highest");

  useEffect(() => {
    if (autoSelectedTab && allowedTabs.includes(autoSelectedTab)) {
      setSelectedTab(autoSelectedTab);
    }
  }, [autoSelectedTab]);

  const apiUsers = userTourCardData?.users ?? [];

  const mappedData = useMemo(
    () =>
      apiUsers.map((u: any) => ({
        name: u.user_name,
        area: u.territory_name,
        deviation: u.deviation_percentage,
      })),
    [apiUsers] // 🟢 direct dependency
  );

  const tabWiseData: Record<string, typeof mappedData> = {
    ZBM: mappedData,
    RBM: mappedData,
    ABM: mappedData,
    TSO: mappedData,
    Self: mappedData,
  };

  const handleTabChange = (_: any, newValue: string) => {
    setSelectedTab(newValue);
    //  Reset sort order whenever tab is changed
    setSortOrder("Highest");

    const commonPayload: any = {
      month: month.toString().padStart(2, "0"),
      year,
      ga_code,
      sort_order: "DESC", // default backend sort for Highest
    };

    if (newValue === "Self") {
      onTabSwitchTour?.({ action: "clear" });
      onTabSwitchUser?.({ action: "clear" });
      return;
    }

    const roleMap: any = {
      ZBM: RolesId.ZONE_MANAGER,
      RBM: RolesId.REGION_MANAGER,
      ABM: RolesId.AREA_MANAGER,
      TSO: RolesId.HEADQUATER_MANAGER,
    };

    const rolePayload = {
      ...commonPayload,
      role_id: roleMap[newValue],
    };

    onTabSwitchTour?.(rolePayload);
    onTabSwitchUser?.(rolePayload);
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    const newSortOrder = event.target.value as "Highest" | "Lowest";
    setSortOrder(newSortOrder);

    const payload: any = {
      month: month.toString().padStart(2, "0"),
      year,
      ga_code,
      sort_order: newSortOrder === "Highest" ? "DESC" : "ASC",
    };

    if (selectedTab === "Self") {
      payload.scope = "self";
      onTabSwitchUser?.(payload); // call user API
      onTabSwitchTour?.(payload); // call tour API
    } else {
      if (selectedTab === "ZBM") payload.role_id = RolesId.ZONE_MANAGER;
      if (selectedTab === "RBM") payload.role_id = RolesId.REGION_MANAGER;
      if (selectedTab === "ABM") payload.role_id = RolesId.AREA_MANAGER;
      if (selectedTab === "TSO") payload.role_id = RolesId.HEADQUATER_MANAGER;

      onTabSwitchUser?.(payload); // call user API
      onTabSwitchTour?.(payload); // call tour API
    }
  };

  const sortedData = [...tabWiseData[selectedTab]].sort((a, b) =>
    sortOrder === "Highest"
      ? b.deviation - a.deviation
      : a.deviation - b.deviation
  );
  const noData = !sortedData || sortedData.length === 0;

  return (
    <Box sx={{ p: 1 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" }, // optional: hides scrollbar
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                minWidth: 90,
                flexShrink: 0, // prevents tab from compressing
              },
            }}
          >
            {allowedTabs.map((tab) => (
              <Tab key={tab} label={tab} value={tab} />
            ))}
          </Tabs>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Sort:
          </Typography>
          <Select
            size="small"
            value={sortOrder}
            onChange={handleSortChange}
            sx={{ minWidth: 160, "& .MuiSelect-select": { py: 0.5 } }}
          >
            <MenuItem value="Highest">Highest to Lowest</MenuItem>
            <MenuItem value="Lowest">Lowest to Highest</MenuItem>
          </Select>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{ borderRadius: 2, boxShadow: "none" }}
      >
        <Table
          sx={{
            tableLayout: "fixed",
            width: "100%",
            "& td, & th": { padding: "6px 16px" },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="left">
                <strong>Name</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Brick/Area/HQ</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Deviation</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {noData ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 2 }}>
                  <Typography color="text.secondary" fontStyle="italic">
                    No deviations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((row) => (
                <TableRow
                  key={row.name + row.area}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    height: 36,
                  }}
                >
                  <TableCell align="left">{row.name}</TableCell>
                  <TableCell align="center">{row.area}</TableCell>
                  <TableCell align="right">
                    <Typography
                      sx={{
                        color:
                          row.deviation < 20
                            ? "green"
                            : row.deviation < 50
                            ? "orange"
                            : "red",
                        fontWeight: 500,
                      }}
                    >
                      {row.deviation}%
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DeviationTable;
